import { APIError } from '@/data'
import AuthApiSingleton from './AuthApi'
import { Constants } from '@/assets'
import { _getItem, _saveAuthInfo, TokenKeys } from '@/modules/Auth/LocalStorageHelper'
import { AuthReturn } from '@/modules/Auth/apiTypes/base'
import { APIRequestConfig, APIRoute } from '@/modules/utils'
import { ChangePassword, CheckToken, Login, MagicLink, RefreshToken, ResetPassword } from '@/modules/Auth/apiTypes'
import { ExtractHydraCollectionMember, HydraCollection, HydraSearchParams } from 'emt-library'
import { runWithEntityManagerClosedRetryAsync } from './EntityManagerClosedRetryHelper'

/* ***********************
 * METHODS
 * *********************** */

export const refreshTokenAsync = async (currentRefreshToken?: string | null): Promise<AuthReturn> => {
  if (!currentRefreshToken) {
    currentRefreshToken = _getItem(TokenKeys.Refresh)
  }

  if (!currentRefreshToken) {
    throw new Error('Refresh token is missing')
  }

  const { token, refresh_token: refreshToken } = await runWithEntityManagerClosedRetryAsync(
    async () =>
      AuthApiSingleton.requestAsync<RefreshToken.Response, RefreshToken.Body>(RefreshToken.Route, {
        body: { refresh_token: currentRefreshToken },
      }),
    'refreshTokenAsync',
  )

  let mercurePublishUrl: string | undefined
  let mercureToken: string | undefined
  try {
    ;({ mercure_url: mercurePublishUrl, mercure_token: mercureToken } = await AuthApiSingleton.requestAsync<
      RefreshToken.MercureResponse,
      RefreshToken.MercureBody
    >(RefreshToken.MercureRoute, {}, token))
  } catch (err) {
    console.error('Cannot refresh Mercure Infos', err.message)
  }

  _saveAuthInfo({ token, refreshToken, mercureToken, mercurePublishUrl })

  return { token, refreshToken, mercurePublishUrl, mercureToken }
}

/**
 * Identify an existing user
 * @param body The params used for the login { email, password } | { email, token }
 * @returns The new Customer combined with Auth information (token)
 */
export const loginAsync = async (body: Login.Body): Promise<Login.Response> => {
  const apiRoute = body.hasOwnProperty('password') ? Login.ClassicRoute : Login.SSORoute
  return await runWithEntityManagerClosedRetryAsync(
    async () => AuthApiSingleton.requestAsync<Login.Response, typeof body>(apiRoute, { body }),
    'loginAsync',
  )
}

/**
 * Route used to ask a reset password
 * @param body The params used for reset { email }
 * @returns True if the request succeeded, False otherwise
 */
export const resetPasswordAsync = async (body: ResetPassword.Body): Promise<boolean> => {
  const res = await runWithEntityManagerClosedRetryAsync(
    async () => AuthApiSingleton.requestAsync<ResetPassword.Response, typeof body>(ResetPassword.Route, { body }),
    'resetPasswordAsync',
  )
  return !!res.success
}

/**
 * Route used to ask a magic link
 * @param body The params used for reset { email }
 * @returns True if the request succeeded, False otherwise
 */
export const magicLinkRequest = async (body: MagicLink.RequestBody): Promise<boolean> => {
  const res = await runWithEntityManagerClosedRetryAsync(
    async () => AuthApiSingleton.requestAsync<MagicLink.RequestResponse, typeof body>(MagicLink.RequestRoute, { body }),
    'magicLinkRequest',
  )
  return !!res.success
}

/**
 * Route used to set new password
 * @param body The params used for generate new password
 * @returns True if the request succeeded, False otherwise
 */
export const changePasswordAsync = async (body: ChangePassword.Body): Promise<void> => {
  await runWithEntityManagerClosedRetryAsync(
    async () => AuthApiSingleton.requestAsync<ChangePassword.Response, typeof body>(ChangePassword.Route, { body }),
    'changePasswordAsync',
  )
}

/**
 * Route to check the token
 * @param body The check token to send
 * @returns True if the request succeeded, False otherwise
 * */
export const checkTokenAsync = async (body: CheckToken.Body): Promise<void> => {
  await runWithEntityManagerClosedRetryAsync(
    async () => AuthApiSingleton.requestAsync<CheckToken.Response, typeof body>(CheckToken.Route, { body }),
    'checkTokenAsync',
  )
}

/**
 * @deprecated
 */
export const requestAuthenticatedAsync = async <T>(
  path: string,
  method: string,
  body?: object | FormData,
  headers?: Record<string, string>,
  doNotAuthenticate?: boolean,
): Promise<T> => {
  let token = doNotAuthenticate ? undefined : _getItem(TokenKeys.Auth)
  try {
    const defaultHeaders = {
      'Content-Type': body instanceof FormData ? 'multipart/form-data' : 'application/json',
      ...headers,
    }
    return await runWithEntityManagerClosedRetryAsync(
      async () => AuthApiSingleton.requestAuthenticatedAsync(path, method, token, body, defaultHeaders),
      'requestAuthenticatedAsync',
    )
  } catch (err) {
    if (!doNotAuthenticate && err instanceof APIError && err.statusCode === 401 && err.message === 'Unauthorized') {
      const result = await refreshTokenAsync()
      if (result?.token) {
        const defaultHeaders = {
          'Content-Type': body instanceof FormData ? 'multipart/form-data' : 'application/json',
          ...headers,
        }
        return await runWithEntityManagerClosedRetryAsync(
          async () => AuthApiSingleton.requestAuthenticatedAsync(path, method, result.token, body, defaultHeaders),
          'requestAuthenticatedAsync(tokenRefresh)',
        )
      }
    }

    throw err
  }
}

/**
 * @deprecated
 */
export const getAllHydraCollectionPagesAsync = async <T>(
  firstUrlPath: string,
  method = 'GET',
  options?: {
    doNotAuthenticate?: boolean
    xAppLocal?: boolean
  },
): Promise<{ count: number; items: T[] }> => {
  let urlPath: string | null = firstUrlPath
  const { doNotAuthenticate = false } = options || {}
  const { xAppLocal = Constants.defaultLocal } = options || {}
  let response: HydraCollection<T> | null
  const items: T[] = []
  let count = 0
  while (urlPath) {
    response = await requestAuthenticatedAsync(
      urlPath,
      method,
      undefined,
      { 'X-App-Locale': xAppLocal },
      doNotAuthenticate,
    )

    if (response) {
      count = response['hydra:totalItems']

      items.push(...response['hydra:member'])

      // Get next page if it exists
      const view = response['hydra:view'] || {}
      urlPath = view['hydra:next']
    } else {
      urlPath = null
    }
  }

  return { count, items }
}

export const requestAuthenticatedV2Async = async <
  ResponseBodyType,
  RequestBodyType extends Record<string, any> | FormData = never,
  RequestUrlPathParams extends Record<string, string> = never,
  RequestUrlSearchParams extends Record<string, any> = never,
>(
  route: APIRoute,
  config: APIRequestConfig<RequestBodyType, RequestUrlPathParams, RequestUrlSearchParams> = {},
  options: {
    doNotAuthenticate?: boolean
  } = {},
): Promise<ResponseBodyType> => {
  const { doNotAuthenticate } = options
  let token = doNotAuthenticate ? undefined : _getItem(TokenKeys.Auth)
  try {
    return await runWithEntityManagerClosedRetryAsync(
      async () => AuthApiSingleton.requestAsync(route, config, token),
      'requestAuthenticatedV2Async',
    )
  } catch (err) {
    console.error('Error in requestAuthenticatedV2Async:', err)
    if (!doNotAuthenticate && err instanceof APIError && err.statusCode === 401 && err.message === 'Unauthorized') {
      const result = await refreshTokenAsync()
      if (result?.token) {
        return await runWithEntityManagerClosedRetryAsync(
          async () => AuthApiSingleton.requestAsync(route, config, result.token),
          'requestAuthenticatedV2Async(tokenRefresh)',
        )
      }
    }

    throw err
  }
}

export const getAllHydraCollectionPagesV2Async = async <
  ResponseBodyType extends HydraCollection<any>,
  RequestBodyType extends Record<string, any> | FormData = never,
  RequestUrlPathParams extends Record<string, string> = never,
  RequestUrlSearchParams extends Record<string, any> = never,
  ResponseItemType = ExtractHydraCollectionMember<ResponseBodyType>,
>(
  route: APIRoute,
  config: APIRequestConfig<RequestBodyType, RequestUrlPathParams, RequestUrlSearchParams> = {},
  options?: {
    doNotAuthenticate?: boolean
    xAppLocal?: boolean
  },
): Promise<{ count: number; items: ResponseItemType[] }> => {
  let urlPath: string | null = route.path
  const { doNotAuthenticate = false, xAppLocal = Constants.defaultLocal } = options || {}
  let response: ResponseBodyType | null
  const items: ResponseItemType[] = []
  let count = 0
  while (urlPath) {
    response = await requestAuthenticatedV2Async(
      {
        ...route,
        path: urlPath,
      },
      {
        ...config,
        searchParams: {
          ...config.searchParams,
          itemsPerPage: 100, // Force large number to avoid making a lot of requests
        } satisfies HydraSearchParams,
        headers: {
          ...config.headers,
          'X-App-Locale': xAppLocal,
        },
      },
      {
        doNotAuthenticate,
      },
    )

    if (response) {
      count = response['hydra:totalItems']

      items.push(...response['hydra:member'])

      // Get next page if it exists
      const view = response['hydra:view'] || {}
      urlPath = view['hydra:next']
    } else {
      urlPath = null
    }
  }

  return { count, items }
}
